import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatCardModule } from "@angular/material/card";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatDividerModule } from "@angular/material/divider";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { UserCredential } from "firebase/auth";
import { finalize, firstValueFrom, from } from "rxjs";
import { AuthService } from "../services/auth.service";
import { ThemeService } from "../services/theme.service";
import enTranslations from "../../assets/i18n/en.json";
import {
  AccountSetupComponent,
  AccountSetupDefaults,
  AccountSetupMode,
  AccountSetupResponse,
} from "./account-setup/account-setup.component";
import { environment } from "../../environments/environment";

function flattenTranslationsObject(value: Record<string, unknown>, prefix = ""): Record<string, string> {
  const result: Record<string, string> = {};
  Object.entries(value).forEach(([key, entry]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      Object.assign(result, flattenTranslationsObject(entry as Record<string, unknown>, path));
    } else if (entry != null) {
      result[path] = String(entry);
    }
  });
  return result;
}

const FALLBACK_TRANSLATIONS = flattenTranslationsObject(enTranslations as Record<string, unknown>);

interface UserProfileResponse {
  email: string | null;
  username: string | null;
  language: string | null;
  salutation: string | null;
  firstName: string | null;
  lastName: string | null;
  accountExists: boolean;
  profileComplete: boolean;
}

@Component({
  selector: "app-sign-in",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    AccountSetupComponent,
  ],
  templateUrl: "./sign-in.component.html",
  styleUrl: "./sign-in.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignInComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);

  private readonly supportedLanguages = new Set(["en", "de", "fr", "es", "ja", "it"]);
  private readonly defaultLanguage = "en";
  private readonly currentLanguage = signal(this.defaultLanguage);
  private readonly translations = signal<Record<string, string>>(FALLBACK_TRANSLATIONS);
  private readonly defaultTranslations = signal<Record<string, string>>(FALLBACK_TRANSLATIONS);

  readonly providerToggles = this.authService.providerToggles;
  readonly showThirdPartyProviders = computed(
    () => this.providerToggles().google || this.providerToggles().facebook || this.providerToggles().apple,
  );
  readonly isCreatingAccount = signal(false);
  readonly submitButtonTranslationKey = computed(() => (this.isCreatingAccount() ? "actions.submit.signUp" : "actions.submit.signIn"));
  readonly providerSectionTranslationKey = computed(() =>
    this.isCreatingAccount() ? "actions.providers.sectionSignUp" : "actions.providers.sectionSignIn",
  );
  readonly modeSwitchLabelTranslationKey = computed(() =>
    this.isCreatingAccount() ? "actions.modeSwitch.signIn" : "actions.modeSwitch.signUp",
  );
  readonly modeSwitchPrefixTranslationKey = computed(() =>
    this.isCreatingAccount() ? "actions.modeSwitch.prefixSignUp" : "actions.modeSwitch.prefixSignIn",
  );

  readonly form = this.fb.nonNullable.group({
    email: ["", [Validators.required, Validators.email]],
    password: ["", [Validators.required, Validators.minLength(8)]],
    confirmPassword: this.fb.nonNullable.control({ value: "", disabled: true }),
  });

  readonly redirectUrl = signal<string | null>(null);
  readonly state = signal<string | null>(null);
  readonly submitInFlight = signal(false);
  readonly accountSetupVisible = signal(false);
  readonly accountSetupMode = signal<AccountSetupMode>("create");
  readonly accountSetupDefaults = signal<AccountSetupDefaults>({});
  readonly pendingIdToken = signal<string | null>(null);
  private readonly pendingCredential = signal<UserCredential | null>(null);
  readonly availableLanguages = Array.from(this.supportedLanguages);
  readonly translateFn = (key: string, params?: Record<string, string | number>) => this.t(key, params);
  readonly privacyPolicyUrl = environment.privacyPolicyUrl;
  readonly termsOfUseUrl = environment.termsOfUseUrl;
  private readonly syncFormDisabledState = effect(() => {
    const disabled = this.submitInFlight();
    const createAccount = this.isCreatingAccount();
    const confirmControl = this.form.controls.confirmPassword;

    if (disabled) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });

    if (createAccount) {
      if (confirmControl.disabled) {
        confirmControl.enable({ emitEvent: false });
      }
    } else if (confirmControl.enabled) {
      confirmControl.disable({ emitEvent: false });
    }
  });
  private readonly errorState = signal<
    { type: "key"; key: string; params?: Record<string, string | number> } | { type: "message"; message: string } | null
  >(null);
  readonly errorMessage = computed(() => {
    const error = this.errorState();
    if (!error) {
      return null;
    }
    return error.type === "key" ? this.t(error.key, error.params) : error.message;
  });

  constructor() {
    this.themeService.init();
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.applyLanguage(params.get("lang"));
      const redirectParam = params.get("redirectUrl");
      const stateParam = params.get("state");
      this.state.set(stateParam);
      this.redirectUrl.set(this.authService.validateRedirectUrl(redirectParam));
    });
  }

  toggleAccountCreation() {
    this.setAccountCreationMode(!this.isCreatingAccount());
  }

  private setAccountCreationMode(createAccount: boolean) {
    this.isCreatingAccount.set(createAccount);
    const confirmControl = this.form.controls.confirmPassword;
    if (createAccount) {
      confirmControl.setValue("");
      confirmControl.setErrors(null);
      confirmControl.enable();
      confirmControl.setValidators([Validators.required]);
      confirmControl.markAsPristine();
      confirmControl.markAsUntouched();
    } else {
      confirmControl.setValue("");
      confirmControl.setErrors(null);
      confirmControl.markAsPristine();
      confirmControl.markAsUntouched();
      confirmControl.disable();
      confirmControl.clearValidators();
    }
    confirmControl.updateValueAndValidity();
    this.errorState.set(null);
  }

  private applyLanguage(requested: string | null) {
    const resolved = this.resolveLanguage(requested);
    if (this.currentLanguage() === resolved) {
      return;
    }
    this.currentLanguage.set(resolved);
    if (resolved === this.defaultLanguage) {
      this.translations.set(this.defaultTranslations());
      return;
    }
    this.translations.set(this.defaultTranslations());
    this.loadLanguage(resolved);
  }

  private resolveLanguage(preferred: string | null): string {
    const fromPreferred = this.normalizeLanguage(preferred);
    if (fromPreferred) {
      return fromPreferred;
    }
    const fromBrowser = this.normalizeLanguage(this.getBrowserLanguage());
    if (fromBrowser) {
      return fromBrowser;
    }
    return this.defaultLanguage;
  }

  private normalizeLanguage(value: string | null): string | null {
    if (!value) {
      return null;
    }
    const shortCode = value.toLowerCase().split("-")[0];
    return this.supportedLanguages.has(shortCode) ? shortCode : null;
  }

  private getBrowserLanguage(): string | null {
    if (typeof navigator === "undefined") {
      return null;
    }
    return navigator.language ?? navigator.languages?.[0] ?? null;
  }

  private loadLanguage(language: string) {
    this.http
      .get<Record<string, unknown>>(`assets/i18n/${language}.json`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          const flattened = flattenTranslationsObject(data);
          this.translations.set(flattened);
        },
        error: (error) => {
          console.error(`Failed to load translations for language ${language}`, error);
          this.translations.set(this.defaultTranslations());
        },
      });
  }

  t(key: string, params: Record<string, string | number> = {}): string {
    const fallback = this.defaultTranslations();
    const active = this.translations();
    const template = active[key] ?? fallback[key] ?? key;
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
      const replacement = params[token];
      return replacement !== undefined ? String(replacement) : match;
    });
  }

  onSubmitEmailPassword() {
    const creatingAccount = this.isCreatingAccount();
    const { email, password, confirmPassword } = this.form.getRawValue();
    const confirmControl = this.form.controls.confirmPassword;
    if (creatingAccount && password !== confirmPassword) {
      confirmControl.setErrors({ ...(confirmControl.errors ?? {}), mismatch: true });
      confirmControl.markAsTouched();
    } else if (creatingAccount && confirmControl.hasError("mismatch")) {
      const { mismatch, ...otherErrors } = confirmControl.errors ?? {};
      const remainingErrors = Object.keys(otherErrors).length ? otherErrors : null;
      confirmControl.setErrors(remainingErrors);
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.errorState.set(null);
    this.submitInFlight.set(true);
    const emailPassword$ = creatingAccount
      ? from(this.authService.createAccountWithEmailPassword(email, password))
      : from(this.authService.signInWithEmailPassword(email, password));
    emailPassword$.pipe(finalize(() => this.submitInFlight.set(false))).subscribe({
      next: (credential) => this.handleSuccess(credential),
      error: (error) => this.handleError(error, creatingAccount ? "signUp" : "signIn"),
    });
  }

  signInWithGoogle() {
    this.triggerProviderSignIn(() => this.authService.signInWithGoogle());
  }

  signInWithFacebook() {
    this.triggerProviderSignIn(() => this.authService.signInWithFacebook());
  }

  signInWithApple() {
    this.triggerProviderSignIn(() => this.authService.signInWithApple());
  }

  private triggerProviderSignIn(fn: () => Promise<UserCredential>) {
    this.errorState.set(null);
    this.submitInFlight.set(true);
    from(fn())
      .pipe(finalize(() => this.submitInFlight.set(false)))
      .subscribe({
        next: (credential) => this.handleSuccess(credential),
        error: (error) => this.handleError(error),
      });
  }

  private async handleSuccess(credential: UserCredential) {
    try {
      await this.ensureProfileComplete(credential);
    } catch (error) {
      this.handleError(error);
    }
  }

  private handleError(error: unknown, context: "signIn" | "signUp" = "signIn") {
    console.error("Authentication failed", error);
    const message = this.resolveFirebaseError(error, context);
    this.errorState.set(message);
  }

  private resolveFirebaseError(
    error: unknown,
    context: "signIn" | "signUp",
  ): { type: "key"; key: string } | { type: "message"; message: string } {
    const genericKey = context === "signUp" ? "errors.firebase.genericSignUp" : "errors.firebase.genericSignIn";
    if (typeof error === "string") {
      return { type: "message", message: error };
    }
    if (error && typeof error === "object" && "code" in error) {
      const code = String((error as { code?: string }).code ?? "");
      switch (code) {
        case "auth/invalid-email":
          return { type: "key", key: "errors.firebase.invalidEmail" };
        case "auth/user-disabled":
          return { type: "key", key: "errors.firebase.userDisabled" };
        case "auth/user-not-found":
        case "auth/wrong-password":
          return { type: "key", key: "errors.firebase.invalidCredentials" };
        case "auth/email-already-in-use":
          return { type: "key", key: "errors.firebase.emailAlreadyInUse" };
        case "auth/weak-password":
          return { type: "key", key: "errors.firebase.weakPassword" };
        case "auth/operation-not-allowed":
          return { type: "key", key: "errors.firebase.operationNotAllowed" };
        case "auth/popup-closed-by-user":
          return { type: "key", key: "errors.firebase.popupClosed" };
        case "auth/cancelled-popup-request":
          return { type: "key", key: "errors.firebase.popupCancelled" };
        case "auth/popup-blocked":
          return { type: "key", key: "errors.firebase.popupBlocked" };
        default:
          return { type: "key", key: genericKey };
      }
    }
    return { type: "key", key: genericKey };
  }

  private async ensureProfileComplete(credential: UserCredential) {
    this.pendingCredential.set(credential);
    const idToken = await credential.user.getIdToken();
    this.pendingIdToken.set(idToken);

    let profile: UserProfileResponse;
    try {
      profile = await firstValueFrom(
        this.http.get<UserProfileResponse>("/api/profile", {
          headers: new HttpHeaders({ Authorization: `Bearer ${idToken}` }),
        }),
      );
    } catch (error) {
      throw error;
    }

    const profileComplete = profile.profileComplete && this.isProfileDataComplete(profile);
    if (profileComplete) {
      await this.authService.finalizeSignIn(credential, this.redirectUrl(), this.state());
      return;
    }

    this.accountSetupMode.set(profile.accountExists ? "update" : "create");
    this.accountSetupDefaults.set({
      username: profile.username ?? "",
      language: profile.language ?? this.currentLanguage(),
      salutation: profile.salutation ?? undefined,
      firstName: profile.firstName ?? "",
      lastName: profile.lastName ?? "",
    });
    this.accountSetupVisible.set(true);
  }

  onAccountSetupCompleted(_response: AccountSetupResponse) {
    const credential = this.pendingCredential();
    if (!credential) {
      return;
    }
    this.accountSetupVisible.set(false);
    this.pendingCredential.set(null);
    this.pendingIdToken.set(null);
    void this.authService.finalizeSignIn(credential, this.redirectUrl(), this.state());
  }

  private isProfileDataComplete(profile: UserProfileResponse): boolean {
    return Boolean(
      profile.username &&
        profile.username.trim().length > 0 &&
        profile.salutation &&
        profile.salutation.trim().length > 0 &&
        profile.firstName &&
        profile.firstName.trim().length > 0 &&
        profile.lastName &&
        profile.lastName.trim().length > 0,
    );
  }
}
